import { ComponentType, lazy, Suspense } from "react";
import { createBrowserRouter, Navigate, Outlet } from "react-router-dom";

import { OrganizationTypeRoute } from "./components/auth/organization-type-route";
import { RoleRoute } from "./components/auth/role-route";
import { AuthRoute } from "./components/auth/route";
import { SettingsRedirect } from "./components/auth/settings-redirect";
import { ErrorPage } from "./components/error/error-page";
import LoadingScreen from "./components/loading";
import RootLayout from "./layouts/root";
import { ManageChannels } from "./pages/channels/manage";
import VariantAttributesPage from "./pages/ecommerce/variant-attributes";
import { BootstrapProvider } from "./providers/bootstrap";
import { useGlobalStore } from "./stores/global";
import { ORGANIZATION_TYPE } from "./types/organization";

// Home landing: the inbox ("My Inbox") is the single home, so conversations are
// always visible and the My Activities cards live in its landing state. Users
// with no workspace (e.g. an org admin not in any workspace) fall back to the
// reports. Runs under BootstrapProvider, so activeWorkspaceId is
// resolved by the time this renders.
const HomeRedirect = () => {
  const activeWorkspaceId = useGlobalStore((state) => state.activeWorkspaceId);

  return (
    <Navigate
      replace
      to={
        activeWorkspaceId ? `/inbox/${activeWorkspaceId}` : "/reports/contacts"
      }
    />
  );
};

const lazyWithRetry = (
  importFn: () => Promise<{ default: ComponentType<unknown> }>,
) =>
  lazy(async () => {
    try {
      return await importFn();
    } catch (error) {
      // One retry handles stale chunks after a deploy/HMR rebuild.
      // If it still fails, propagate so react-router's errorElement renders.
      try {
        return await importFn();
      } catch {
        throw error;
      }
    }
  });

const AppLayout = lazyWithRetry(() =>
  import("./layouts/app").then((module) => ({ default: module.AppLayout })),
);
const IndexPage = lazyWithRetry(() => import("./pages"));
const InboxPage = lazyWithRetry(() =>
  import("./pages/inbox/[workspaceId]/[contactId]").then((module) => ({
    default: module.InboxPage,
  })),
);
const MyInboxPage = lazyWithRetry(() =>
  import("./pages/inbox/[workspaceId]/activities").then((module) => ({
    default: module.MyInboxPage,
  })),
);
const AcceptInvitePage = lazyWithRetry(() =>
  import("./pages/auth/accept-invite").then((module) => ({
    default: module.AcceptInvitePage,
  })),
);
const ForgotPasswordPage = lazyWithRetry(() =>
  import("./pages/auth/forgot-password").then((module) => ({
    default: module.ForgotPasswordPage,
  })),
);
const OnboardingPage = lazyWithRetry(() =>
  import("./pages/auth/onboarding").then((module) => ({
    default: module.OnboardingPage,
  })),
);
const SignInPage = lazyWithRetry(() =>
  import("./pages/auth/sign-in").then((module) => ({
    default: module.SignInPage,
  })),
);
const SignOutPage = lazyWithRetry(() =>
  import("./pages/auth/sign-out").then((module) => ({
    default: module.SignOutPage,
  })),
);
const SignUpPage = lazyWithRetry(() =>
  import("./pages/auth/sign-up").then((module) => ({
    default: module.SignUpPage,
  })),
);
const SSOCallbackPage = lazyWithRetry(() =>
  import("./pages/auth/sso-callback").then((module) => ({
    default: module.SSOCallbackPage,
  })),
);
const JoinOrganizationPage = lazyWithRetry(() =>
  import("./pages/auth/join-organization").then((module) => ({
    default: module.JoinOrganizationPage,
  })),
);
const AcceptInvitationPage = lazyWithRetry(() =>
  import("./pages/auth/accept-invitation").then((module) => ({
    default: module.AcceptInvitationPage,
  })),
);
const ContactsPage = lazyWithRetry(() =>
  import("./pages/contacts").then((module) => ({
    default: module.ContactsPage,
  })),
);
const ContactDetailPage = lazyWithRetry(() =>
  import("./pages/contact-detail").then((module) => ({
    default: module.ContactDetailPage,
  })),
);
const TicketsPage = lazyWithRetry(() =>
  import("./pages/tickets").then((module) => ({
    default: module.TicketsPage,
  })),
);
const BroadcastsPage = lazyWithRetry(() =>
  import("./pages/broadcasts").then((module) => ({
    default: module.BroadcastsPage,
  })),
);
const BroadcastCreatePage = lazyWithRetry(() =>
  import("./pages/broadcasts/create").then((module) => ({
    default: module.BroadcastCreatePage,
  })),
);
const BroadcastDetailPage = lazyWithRetry(() =>
  import("./pages/broadcasts/[id]").then((module) => ({
    default: module.BroadcastDetailPage,
  })),
);
const BroadcastEditPage = lazyWithRetry(() =>
  import("./pages/broadcasts/[id]/edit").then((module) => ({
    default: module.BroadcastEditPage,
  })),
);

const WorkflowsPage = lazyWithRetry(() =>
  import("./pages/workflows").then((module) => ({
    default: module.WorkflowsPage,
  })),
);
const WorkflowCreatePage = lazyWithRetry(() =>
  import("./pages/workflows/create").then((module) => ({
    default: module.WorkflowCreatePage,
  })),
);
const WorkflowEditPage = lazyWithRetry(() =>
  import("./pages/workflows/[id]").then((module) => ({
    default: module.WorkflowEditPage,
  })),
);
const WorkflowActivitiesPage = lazyWithRetry(() =>
  import("./pages/workflows/[id]/activities").then((module) => ({
    default: module.WorkflowActivitiesPage,
  })),
);

const AdsPage = lazyWithRetry(() =>
  import("./pages/ads").then((module) => ({
    default: module.AdsPage,
  })),
);

const ReportsLayout = lazyWithRetry(() =>
  import("./pages/reports").then((module) => ({
    default: module.ReportsPage,
  })),
);
const ContactsReportsPage = lazyWithRetry(() =>
  import("./pages/reports/contacts").then((module) => ({
    default: module.default,
  })),
);
const ConversationsReportsPage = lazyWithRetry(() =>
  import("./pages/reports/conversations").then((module) => ({
    default: module.default,
  })),
);
const OrdersReportsPage = lazyWithRetry(() =>
  import("./pages/reports/orders").then((module) => ({
    default: module.default,
  })),
);
const ProductsReportsPage = lazyWithRetry(() =>
  import("./pages/reports/products").then((module) => ({
    default: module.default,
  })),
);
const SalesReportsPage = lazyWithRetry(() =>
  import("./pages/reports/sales").then((module) => ({
    default: module.default,
  })),
);
const OrganizationPage = lazyWithRetry(() =>
  import("./pages/settings/organization").then((module) => ({
    default: module.default,
  })),
);
const SnippetsPage = lazyWithRetry(() =>
  import("./pages/settings/snippets").then((module) => ({
    default: module.default,
  })),
);
const FilesPage = lazyWithRetry(() =>
  import("./pages/settings/files").then((module) => ({
    default: module.default,
  })),
);
const UsersPage = lazyWithRetry(() =>
  import("./pages/settings/users").then((module) => ({
    default: module.default,
  })),
);
const DeveloperPage = lazyWithRetry(() =>
  import("./pages/settings/developer").then((module) => ({
    default: module.default,
  })),
);
const SubscriptionPage = lazyWithRetry(() =>
  import("./pages/settings/subscription").then((module) => ({
    default: module.default,
  })),
);
const LifecyclePage = lazyWithRetry(() =>
  import("./pages/settings/lifecycle").then((module) => ({
    default: module.default,
  })),
);
const ClosingNotesPage = lazyWithRetry(() =>
  import("./pages/settings/closing-notes").then((module) => ({
    default: module.default,
  })),
);
const WrapUpPage = lazyWithRetry(() =>
  import("./pages/settings/wrap-up").then((module) => ({
    default: module.default,
  })),
);
const TagsPage = lazyWithRetry(() =>
  import("./pages/settings/tags").then((module) => ({
    default: module.default,
  })),
);
const ContactSourcesPage = lazyWithRetry(() =>
  import("./pages/settings/contact-sources").then((module) => ({
    default: module.default,
  })),
);
const WebsitesPage = lazyWithRetry(() =>
  import("./pages/settings/websites").then((module) => ({
    default: module.default,
  })),
);
const WorkspaceSettingsPage = lazyWithRetry(() =>
  import("./pages/settings/workspace").then((module) => ({
    default: module.default,
  })),
);
const WorkspaceUsersPage = lazyWithRetry(() =>
  import("./pages/settings/workspace-users").then((module) => ({
    default: module.default,
  })),
);
const TeamsPage = lazyWithRetry(() =>
  import("./pages/settings/teams").then((module) => ({
    default: module.default,
  })),
);
const AgentStudioPage = lazyWithRetry(() =>
  import("./pages/settings/ai-agents/[agentId]").then((module) => ({
    default: module.default,
  })),
);
const AgentQuickReplyPage = lazyWithRetry(() =>
  import("./pages/settings/ai-agents/[agentId]/quick-reply").then((module) => ({
    default: module.default,
  })),
);
const AgentFilesPage = lazyWithRetry(() =>
  import("./pages/settings/ai-agents/[agentId]/files").then((module) => ({
    default: module.default,
  })),
);
const AgentTestsPage = lazyWithRetry(() =>
  import("./pages/settings/ai-agents/[agentId]/tests").then((module) => ({
    default: module.default,
  })),
);
const AgentTestNewPage = lazyWithRetry(() =>
  import("./pages/settings/ai-agents/[agentId]/tests/new").then((module) => ({
    default: module.default,
  })),
);
const AgentTestEditPage = lazyWithRetry(() =>
  import("./pages/settings/ai-agents/[agentId]/tests/[testId]").then(
    (module) => ({
      default: module.default,
    }),
  ),
);
const AgentRunsPage = lazyWithRetry(() =>
  import("./pages/settings/ai-agents/[agentId]/runs").then((module) => ({
    default: module.default,
  })),
);
const AgentRunDetailPage = lazyWithRetry(() =>
  import("./pages/settings/ai-agents/[agentId]/runs/[runId]").then(
    (module) => ({
      default: module.default,
    }),
  ),
);
const AgentAvailabilityPage = lazyWithRetry(() =>
  import("./pages/settings/ai-agents/[agentId]/availability").then((module) => ({
    default: module.default,
  })),
);
const BusinessHoursPage = lazyWithRetry(() =>
  import("./pages/settings/business-hours").then((module) => ({
    default: module.default,
  })),
);
const ProfilePage = lazyWithRetry(() => import("./pages/profile"));
const ChannelsPage = lazyWithRetry(() =>
  import("./pages/channels/index").then((module) => ({
    default: module.ChannelsPage,
  })),
);
const CreateChannelPage = lazyWithRetry(() =>
  import("./pages/channels/create/index").then((module) => ({
    default: module.CreateChannelPage,
  })),
);
const CreateInstagramChannelPage = lazyWithRetry(() =>
  import("./pages/channels/create/instagram").then((module) => ({
    default: module.CreateInstagramChannelPage,
  })),
);
const CreateWhatsAppChannelPage = lazyWithRetry(() =>
  import("./pages/channels/create/whatsapp").then((module) => ({
    default: module.CreateWhatsAppChannelPage,
  })),
);
const CreateWhatsAppCoexistencePage = lazyWithRetry(() =>
  import("./pages/channels/create/whatsapp-coexistence").then((module) => ({
    default: module.CreateWhatsAppCoexistencePage,
  })),
);
const CreateMessengerChannelPage = lazyWithRetry(() =>
  import("./pages/channels/create/messenger").then((module) => ({
    default: module.CreateMessengerChannelPage,
  })),
);
const CreateTikTokChannelPage = lazyWithRetry(() =>
  import("./pages/channels/create/tiktok").then((module) => ({
    default: module.CreateTikTokChannelPage,
  })),
);
const ChannelConfiguration = lazyWithRetry(() =>
  import("./pages/channels/manage/configuration").then((module) => ({
    default: module.ChannelConfiguration,
  })),
);
const OAuthCallbackHandler = lazyWithRetry(() =>
  import("./pages/channels/manage/oauth-callback").then((module) => ({
    default: module.OAuthCallbackHandler,
  })),
);
const WhatsappProfilePage = lazyWithRetry(
  () => import("./pages/channels/manage/whatsapp/profile"),
);
const WhatsappTemplatesListPage = lazyWithRetry(
  () => import("./pages/channels/manage/whatsapp/templates/list"),
);
const WhatsappTemplatesEditPage = lazyWithRetry(
  () => import("./pages/channels/manage/whatsapp/templates/edit"),
);
const InstagramFeedPage = lazyWithRetry(() =>
  import("./pages/channels/manage/instagram/feed").then((module) => ({
    default: module.default,
  })),
);
const MessengerPostsPage = lazyWithRetry(() =>
  import("./pages/channels/manage/messenger/posts").then((module) => ({
    default: module.default,
  })),
);
const WelcomeMessagesListPage = lazyWithRetry(
  () => import("./pages/channels/manage/welcome-messages/list"),
);
const WelcomeMessagesEditPage = lazyWithRetry(
  () => import("./pages/channels/manage/welcome-messages/edit"),
);
const PersistentMenuPage = lazyWithRetry(
  () => import("./pages/channels/manage/persistent-menu"),
);
const EcommercePage = lazyWithRetry(() =>
  import("./pages/ecommerce").then((module) => ({
    default: module.EcommercePage,
  })),
);
const ProductsPage = lazyWithRetry(() =>
  import("./pages/ecommerce/products").then((module) => ({
    default: module.ProductsPage,
  })),
);
const OrdersPage = lazyWithRetry(() =>
  import("./pages/ecommerce/orders").then((module) => ({
    default: module.OrdersPage,
  })),
);
const ProductCreatePage = lazyWithRetry(() =>
  import("./pages/ecommerce/products/create").then((module) => ({
    default: module.ProductCreatePage,
  })),
);
const ProductEditPage = lazyWithRetry(() =>
  import("./pages/ecommerce/products/edit").then((module) => ({
    default: module.ProductEditPage,
  })),
);
const ProductVariantsPage = lazyWithRetry(() =>
  import("./pages/ecommerce/products/variants").then((module) => ({
    default: module.ProductVariantsPage,
  })),
);
const CategoriesPage = lazyWithRetry(() =>
  import("./pages/ecommerce/categories").then((module) => ({
    default: module.CategoriesPage,
  })),
);
const BrandsPage = lazyWithRetry(() =>
  import("./pages/ecommerce/brands").then((module) => ({
    default: module.BrandsPage,
  })),
);
const StorePage = lazyWithRetry(() =>
  import("./pages/ecommerce/store").then((module) => ({
    default: module.StorePage,
  })),
);

const IntegrationsPage = lazyWithRetry(() =>
  import("./pages/integrations/index").then((module) => ({
    default: module.IntegrationsPage,
  })),
);

const DoctorsPage = lazyWithRetry(() =>
  import("./pages/clinics/doctors").then((module) => ({
    default: module.DoctorsPage,
  })),
);
const TreatmentsPage = lazyWithRetry(() =>
  import("./pages/clinics/treatments").then((module) => ({
    default: module.TreatmentsPage,
  })),
);
const AppointmentsPage = lazyWithRetry(() =>
  import("./pages/clinics/appointments").then((module) => ({
    default: module.AppointmentsPage,
  })),
);
const DepartmentsPage = lazyWithRetry(() =>
  import("./pages/clinics/departments").then((module) => ({
    default: module.DepartmentsPage,
  })),
);

// Create router as a singleton to prevent multiple instances during HMR
let routerInstance: ReturnType<typeof createBrowserRouter> | null = null;

export const router = (() => {
  if (!routerInstance) {
    routerInstance = createBrowserRouter([
      {
        path: "/",
        errorElement: <ErrorPage />,
        element: <RootLayout />,
        children: [
          {
            index: true,
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <IndexPage />
              </Suspense>
            ),
          },
          {
            path: "auth/sso-callback",
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <SSOCallbackPage />
              </Suspense>
            ),
          },
          {
            path: "auth/sign-up",
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <SignUpPage />
              </Suspense>
            ),
          },
          {
            path: "auth/sign-in",
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <SignInPage />
              </Suspense>
            ),
          },
          {
            path: "auth/sign-out",
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <SignOutPage />
              </Suspense>
            ),
          },
          {
            path: "auth/forgot-password",
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <ForgotPasswordPage />
              </Suspense>
            ),
          },
          {
            path: "auth/accept-invite",
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <AcceptInvitePage />
              </Suspense>
            ),
          },
          {
            path: "join-organization",
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <JoinOrganizationPage />
              </Suspense>
            ),
          },
          {
            path: "accept-invitation",
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <AcceptInvitationPage />
              </Suspense>
            ),
          },
          {
            element: <AuthRoute />,
            children: [
              {
                path: "auth/onboarding",
                element: (
                  <Suspense fallback={<LoadingScreen />}>
                    <OnboardingPage />
                  </Suspense>
                ),
              },

              {
                path: "",
                element: (
                  <BootstrapProvider>
                    <Suspense fallback={<LoadingScreen />}>
                      <AppLayout />
                    </Suspense>
                  </BootstrapProvider>
                ),
                children: [
                  {
                    element: <RoleRoute />,
                    children: [
                      {
                        // Dashboard is gone as a landing — "My Inbox" is the
                        // home now. Keep the old path as a redirect so existing
                        // links / bookmarks land on the inbox (My Activities
                        // cards show in the inbox's no-conversation state).
                        path: "dashboard",
                        element: <HomeRedirect />,
                      },
                      {
                        path: "inbox/:workspaceId",
                        children: [
                          {
                            index: true,
                            element: (
                              <Suspense fallback={<LoadingScreen />}>
                                <InboxPage />
                              </Suspense>
                            ),
                          },
                          {
                            // "My Inbox" — personal activity cards. Static
                            // segment, so it wins over the :contactId param.
                            path: "activities",
                            element: (
                              <Suspense fallback={<LoadingScreen />}>
                                <MyInboxPage />
                              </Suspense>
                            ),
                          },
                          {
                            path: ":contactId",
                            element: (
                              <Suspense fallback={<LoadingScreen />}>
                                <InboxPage />
                              </Suspense>
                            ),
                          },
                        ],
                      },
                      {
                        path: "reports",
                        element: (
                          <Suspense fallback={<LoadingScreen />}>
                            <ReportsLayout />
                          </Suspense>
                        ),
                        children: [
                          {
                            index: true,
                            element: (
                              <Navigate replace to="/reports/contacts" />
                            ),
                          },
                          {
                            path: "contacts",
                            element: (
                              <Suspense fallback={<LoadingScreen />}>
                                <ContactsReportsPage />
                              </Suspense>
                            ),
                          },
                          {
                            path: "conversations",
                            element: (
                              <Suspense fallback={<LoadingScreen />}>
                                <ConversationsReportsPage />
                              </Suspense>
                            ),
                          },
                          {
                            path: "orders",
                            element: (
                              <Suspense fallback={<LoadingScreen />}>
                                <OrdersReportsPage />
                              </Suspense>
                            ),
                          },
                          {
                            path: "products",
                            element: (
                              <Suspense fallback={<LoadingScreen />}>
                                <ProductsReportsPage />
                              </Suspense>
                            ),
                          },
                          {
                            element: (
                              <OrganizationTypeRoute
                                allowedType={ORGANIZATION_TYPE.ECOMMERCE}
                              />
                            ),
                            children: [
                              {
                                path: "sales",
                                element: (
                                  <Suspense fallback={<LoadingScreen />}>
                                    <SalesReportsPage />
                                  </Suspense>
                                ),
                              },
                            ],
                          },
                          {
                            element: (
                              <OrganizationTypeRoute
                                allowedType={ORGANIZATION_TYPE.CLINIC}
                              />
                            ),
                            children: [
                              {
                                path: "sales",
                                element: (
                                  <Suspense fallback={<LoadingScreen />}>
                                    <SalesReportsPage />
                                  </Suspense>
                                ),
                              },
                            ],
                          },
                        ],
                      },
                      {
                        path: "contacts",
                        element: (
                          <Suspense fallback={<LoadingScreen />}>
                            <ContactsPage />
                          </Suspense>
                        ),
                      },
                      {
                        path: "contacts/:contactId",
                        element: (
                          <Suspense fallback={<LoadingScreen />}>
                            <ContactDetailPage />
                          </Suspense>
                        ),
                      },

                      {
                        path: "tickets",
                        element: (
                          <Suspense fallback={<LoadingScreen />}>
                            <TicketsPage />
                          </Suspense>
                        ),
                      },
                      {
                        path: "broadcasts",
                        element: (
                          <Suspense fallback={<LoadingScreen />}>
                            <BroadcastsPage />
                          </Suspense>
                        ),
                      },
                      {
                        path: "broadcasts/create",
                        element: (
                          <Suspense fallback={<LoadingScreen />}>
                            <BroadcastCreatePage />
                          </Suspense>
                        ),
                      },
                      {
                        path: "broadcasts/:id",
                        element: (
                          <Suspense fallback={<LoadingScreen />}>
                            <BroadcastDetailPage />
                          </Suspense>
                        ),
                      },
                      {
                        path: "broadcasts/:id/edit",
                        element: (
                          <Suspense fallback={<LoadingScreen />}>
                            <BroadcastEditPage />
                          </Suspense>
                        ),
                      },
                      {
                        path: "workflows",
                        element: (
                          <Suspense fallback={<LoadingScreen />}>
                            <WorkflowsPage />
                          </Suspense>
                        ),
                      },
                      {
                        path: "workflows/create",
                        element: (
                          <Suspense fallback={<LoadingScreen />}>
                            <WorkflowCreatePage />
                          </Suspense>
                        ),
                      },
                      {
                        path: "workflows/:id",
                        element: (
                          <Suspense fallback={<LoadingScreen />}>
                            <WorkflowEditPage />
                          </Suspense>
                        ),
                      },
                      {
                        path: "workflows/:id/activities",
                        element: (
                          <Suspense fallback={<LoadingScreen />}>
                            <WorkflowActivitiesPage />
                          </Suspense>
                        ),
                      },
                      {
                        path: "ads",
                        element: (
                          <Suspense fallback={<LoadingScreen />}>
                            <AdsPage />
                          </Suspense>
                        ),
                      },
                      {
                        // Single agent per workspace → the id lives in the
                        // agent query, not the URL. `/sophus` IS the studio
                        // (or the create form when no agent exists yet).
                        path: "sophus",
                        element: (
                          <Suspense fallback={<LoadingScreen />}>
                            <AgentStudioPage />
                          </Suspense>
                        ),
                      },
                      {
                        path: "sophus/quick-reply",
                        element: (
                          <Suspense fallback={<LoadingScreen />}>
                            <AgentQuickReplyPage />
                          </Suspense>
                        ),
                      },
                      {
                        path: "sophus/files",
                        element: (
                          <Suspense fallback={<LoadingScreen />}>
                            <AgentFilesPage />
                          </Suspense>
                        ),
                      },
                      {
                        path: "sophus/tests",
                        element: (
                          <Suspense fallback={<LoadingScreen />}>
                            <AgentTestsPage />
                          </Suspense>
                        ),
                      },
                      {
                        path: "sophus/tests/new",
                        element: (
                          <Suspense fallback={<LoadingScreen />}>
                            <AgentTestNewPage />
                          </Suspense>
                        ),
                      },
                      {
                        path: "sophus/tests/:testId",
                        element: (
                          <Suspense fallback={<LoadingScreen />}>
                            <AgentTestEditPage />
                          </Suspense>
                        ),
                      },
                      {
                        path: "sophus/runs",
                        element: (
                          <Suspense fallback={<LoadingScreen />}>
                            <AgentRunsPage />
                          </Suspense>
                        ),
                      },
                      {
                        path: "sophus/runs/:runId",
                        element: (
                          <Suspense fallback={<LoadingScreen />}>
                            <AgentRunDetailPage />
                          </Suspense>
                        ),
                      },
                      {
                        path: "sophus/availability",
                        element: (
                          <Suspense fallback={<LoadingScreen />}>
                            <AgentAvailabilityPage />
                          </Suspense>
                        ),
                      },
                      {
                        // Legacy links (id-carrying) → the new id-less route.
                        path: "ai-agents",
                        element: <Navigate replace to="/sophus" />,
                      },
                      {
                        path: "ai-agents/*",
                        element: <Navigate replace to="/sophus" />,
                      },
                      {
                        element: (
                          <OrganizationTypeRoute
                            allowedType={ORGANIZATION_TYPE.CLINIC}
                          />
                        ),
                        children: [
                          {
                            path: "treatments",
                            element: (
                              <Suspense fallback={<LoadingScreen />}>
                                <TreatmentsPage />
                              </Suspense>
                            ),
                          },
                          {
                            path: "doctors",
                            element: (
                              <Suspense fallback={<LoadingScreen />}>
                                <DoctorsPage />
                              </Suspense>
                            ),
                          },
                          {
                            path: "appointments",
                            element: (
                              <Suspense fallback={<LoadingScreen />}>
                                <AppointmentsPage />
                              </Suspense>
                            ),
                          },
                          {
                            path: "departments",
                            element: (
                              <Suspense fallback={<LoadingScreen />}>
                                <DepartmentsPage />
                              </Suspense>
                            ),
                          },
                        ],
                      },
                      {
                        path: "channels",
                        element: (
                          <Suspense fallback={<LoadingScreen />}>
                            <ChannelsPage />
                          </Suspense>
                        ),
                      },
                      {
                        path: "channels/create",
                        element: (
                          <Suspense fallback={<LoadingScreen />}>
                            <CreateChannelPage />
                          </Suspense>
                        ),
                      },
                      {
                        path: "channels/manage",
                        element: (
                          <Suspense fallback={<LoadingScreen />}>
                            <OAuthCallbackHandler />
                          </Suspense>
                        ),
                      },
                      {
                        path: "channels/manage/:id",
                        element: (
                          <Suspense fallback={<LoadingScreen />}>
                            <ManageChannels />
                          </Suspense>
                        ),
                        children: [
                          {
                            index: true,
                            element: (
                              <Suspense fallback={<LoadingScreen />}>
                                <ChannelConfiguration />
                              </Suspense>
                            ),
                          },
                          {
                            path: "whatsapp-profile",
                            element: (
                              <Suspense fallback={<LoadingScreen />}>
                                <WhatsappProfilePage />
                              </Suspense>
                            ),
                          },
                          {
                            path: "whatsapp-templates",
                            element: (
                              <Suspense fallback={<LoadingScreen />}>
                                <WhatsappTemplatesListPage />
                              </Suspense>
                            ),
                          },
                          {
                            path: "whatsapp-templates/create",
                            element: (
                              <Suspense fallback={<LoadingScreen />}>
                                <WhatsappTemplatesEditPage />
                              </Suspense>
                            ),
                          },
                          {
                            path: "whatsapp-templates/edit/:templateId",
                            element: (
                              <Suspense fallback={<LoadingScreen />}>
                                <WhatsappTemplatesEditPage />
                              </Suspense>
                            ),
                          },
                          {
                            path: "instagram-feed",
                            element: (
                              <Suspense fallback={<LoadingScreen />}>
                                <InstagramFeedPage />
                              </Suspense>
                            ),
                          },
                          {
                            path: "posts",
                            element: (
                              <Suspense fallback={<LoadingScreen />}>
                                <MessengerPostsPage />
                              </Suspense>
                            ),
                          },
                          {
                            path: "welcome-messages",
                            element: (
                              <Suspense fallback={<LoadingScreen />}>
                                <WelcomeMessagesListPage />
                              </Suspense>
                            ),
                          },
                          {
                            path: "welcome-messages/create",
                            element: (
                              <Suspense fallback={<LoadingScreen />}>
                                <WelcomeMessagesEditPage />
                              </Suspense>
                            ),
                          },
                          {
                            path: "welcome-messages/edit/:messageId",
                            element: (
                              <Suspense fallback={<LoadingScreen />}>
                                <WelcomeMessagesEditPage />
                              </Suspense>
                            ),
                          },
                          {
                            path: "persistent-menu",
                            element: (
                              <Suspense fallback={<LoadingScreen />}>
                                <PersistentMenuPage />
                              </Suspense>
                            ),
                          },
                        ],
                      },
                      {
                        path: "channels/create/instagram",
                        element: (
                          <Suspense fallback={<LoadingScreen />}>
                            <CreateInstagramChannelPage />
                          </Suspense>
                        ),
                      },
                      {
                        path: "channels/create/whatsapp",
                        element: (
                          <Suspense fallback={<LoadingScreen />}>
                            <CreateWhatsAppChannelPage />
                          </Suspense>
                        ),
                      },
                      {
                        path: "channels/create/whatsapp-coexistence",
                        element: (
                          <Suspense fallback={<LoadingScreen />}>
                            <CreateWhatsAppCoexistencePage />
                          </Suspense>
                        ),
                      },
                      {
                        path: "channels/create/messenger",
                        element: (
                          <Suspense fallback={<LoadingScreen />}>
                            <CreateMessengerChannelPage />
                          </Suspense>
                        ),
                      },
                      {
                        path: "channels/create/tiktok",
                        element: (
                          <Suspense fallback={<LoadingScreen />}>
                            <CreateTikTokChannelPage />
                          </Suspense>
                        ),
                      },
                      {
                        path: "integrations",
                        element: (
                          <Suspense fallback={<LoadingScreen />}>
                            <IntegrationsPage />
                          </Suspense>
                        ),
                      },
                      {
                        path: "profile",
                        element: (
                          <Suspense fallback={<LoadingScreen />}>
                            <ProfilePage />
                          </Suspense>
                        ),
                      },
                      {
                        element: (
                          <OrganizationTypeRoute
                            allowedType={ORGANIZATION_TYPE.ECOMMERCE}
                          />
                        ),
                        children: [
                          {
                            path: "ecommerce",
                            element: (
                              <Suspense fallback={<LoadingScreen />}>
                                <EcommercePage />
                              </Suspense>
                            ),
                            children: [
                              {
                                index: true,
                                element: (
                                  <Navigate replace to="/ecommerce/products" />
                                ),
                              },
                              {
                                path: "products",
                                element: (
                                  <Suspense fallback={<LoadingScreen />}>
                                    <ProductsPage />
                                  </Suspense>
                                ),
                              },
                              {
                                path: "categories",
                                element: (
                                  <Suspense fallback={<LoadingScreen />}>
                                    <CategoriesPage />
                                  </Suspense>
                                ),
                              },
                              {
                                path: "brands",
                                element: (
                                  <Suspense fallback={<LoadingScreen />}>
                                    <BrandsPage />
                                  </Suspense>
                                ),
                              },
                              {
                                path: "orders",
                                element: (
                                  <Suspense fallback={<LoadingScreen />}>
                                    <OrdersPage />
                                  </Suspense>
                                ),
                              },
                              {
                                path: "variant-attributes",
                                element: (
                                  <Suspense fallback={<LoadingScreen />}>
                                    <VariantAttributesPage />
                                  </Suspense>
                                ),
                              },
                              {
                                path: "store",
                                element: (
                                  <Suspense fallback={<LoadingScreen />}>
                                    <StorePage />
                                  </Suspense>
                                ),
                              },
                            ],
                          },
                          {
                            path: "ecommerce/products/create",
                            element: (
                              <Suspense fallback={<LoadingScreen />}>
                                <ProductCreatePage />
                              </Suspense>
                            ),
                          },
                          {
                            path: "ecommerce/products/:productId/edit",
                            element: (
                              <Suspense fallback={<LoadingScreen />}>
                                <ProductEditPage />
                              </Suspense>
                            ),
                          },
                          {
                            path: "ecommerce/products/:productId/variants",
                            element: (
                              <Suspense fallback={<LoadingScreen />}>
                                <ProductVariantsPage />
                              </Suspense>
                            ),
                          },
                          {
                            path: "ecommerce/products/:productId/variants/:variantId",
                            element: (
                              <Suspense fallback={<LoadingScreen />}>
                                <ProductVariantsPage />
                              </Suspense>
                            ),
                          },
                        ],
                      },
                      {
                        path: "settings",
                        element: <Outlet />,
                        children: [
                          {
                            index: true,
                            element: <SettingsRedirect />,
                          },
                          {
                            path: "organization",
                            element: (
                              <Suspense fallback={<LoadingScreen />}>
                                <OrganizationPage />
                              </Suspense>
                            ),
                          },
                          {
                            path: "snippets",
                            element: (
                              <Suspense fallback={<LoadingScreen />}>
                                <SnippetsPage />
                              </Suspense>
                            ),
                          },
                          {
                            path: "files",
                            element: (
                              <Suspense fallback={<LoadingScreen />}>
                                <FilesPage />
                              </Suspense>
                            ),
                          },
                          {
                            path: "users",
                            element: (
                              <Suspense fallback={<LoadingScreen />}>
                                <UsersPage />
                              </Suspense>
                            ),
                          },
                          {
                            path: "subscription",
                            element: (
                              <Suspense fallback={<LoadingScreen />}>
                                <SubscriptionPage />
                              </Suspense>
                            ),
                          },
                          {
                            path: "developer",
                            element: (
                              <Suspense fallback={<LoadingScreen />}>
                                <DeveloperPage />
                              </Suspense>
                            ),
                          },
                          {
                            path: "lifecycle",
                            element: (
                              <Suspense fallback={<LoadingScreen />}>
                                <LifecyclePage />
                              </Suspense>
                            ),
                          },
                          {
                            path: "closing-notes",
                            element: (
                              <Suspense fallback={<LoadingScreen />}>
                                <ClosingNotesPage />
                              </Suspense>
                            ),
                          },
                          {
                            path: "wrap-up",
                            element: (
                              <Suspense fallback={<LoadingScreen />}>
                                <WrapUpPage />
                              </Suspense>
                            ),
                          },
                          {
                            path: "tags",
                            element: (
                              <Suspense fallback={<LoadingScreen />}>
                                <TagsPage />
                              </Suspense>
                            ),
                          },
                          {
                            path: "contact-sources",
                            element: (
                              <Suspense fallback={<LoadingScreen />}>
                                <ContactSourcesPage />
                              </Suspense>
                            ),
                          },
                          {
                            path: "websites",
                            element: (
                              <Suspense fallback={<LoadingScreen />}>
                                <WebsitesPage />
                              </Suspense>
                            ),
                          },
                          {
                            path: "workspace",
                            element: (
                              <Suspense fallback={<LoadingScreen />}>
                                <WorkspaceSettingsPage />
                              </Suspense>
                            ),
                          },
                          {
                            path: "workspace-users",
                            element: (
                              <Suspense fallback={<LoadingScreen />}>
                                <WorkspaceUsersPage />
                              </Suspense>
                            ),
                          },
                          {
                            path: "teams",
                            element: (
                              <Suspense fallback={<LoadingScreen />}>
                                <TeamsPage />
                              </Suspense>
                            ),
                          },
                          {
                            path: "business-hours",
                            element: (
                              <Suspense fallback={<LoadingScreen />}>
                                <BusinessHoursPage />
                              </Suspense>
                            ),
                          },
                        ],
                      },
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },
    ]);
  }

  return routerInstance;
})();
